const resizeObserverLoopErr = /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/i;

// 1. Definitively patch ResizeObserver at the source
if (typeof window !== 'undefined' && window.ResizeObserver) {
  const OriginalResizeObserver = window.ResizeObserver;
  window.ResizeObserver = class ResizeObserver extends OriginalResizeObserver {
    constructor(callback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => {
          try {
            callback(entries, observer);
          } catch (e) {
            // Silence any errors within the callback
          }
        });
      });
    }
  };
}

// 2. Override console.error for any strings that slip through
const originalError = console.error;
console.error = (...args) => {
  if (args[0]) {
    const msg = typeof args[0] === 'string' ? args[0] : (args[0].message || '');
    if (resizeObserverLoopErr.test(msg)) return;
  }
  originalError(...args);
};

// 3. Catch global runtime errors (capture phase runs before CRA error overlay)
window.addEventListener("error", (e) => {
  if (resizeObserverLoopErr.test(e.message) || resizeObserverLoopErr.test(e.error?.message)) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
  // Suppress cross-origin "Script error." from Monaco editor web workers
  if (e.message === 'Script error.' && !e.filename) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
}, true);

// 4. Handle unhandled promise rejections
window.addEventListener("unhandledrejection", (e) => {
  if (resizeObserverLoopErr.test(e.reason?.message) || resizeObserverLoopErr.test(e.reason)) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
});

// 5. Traditional window.onerror
window.onerror = (message, source) => {
  if (typeof message === 'string' && resizeObserverLoopErr.test(message)) {
    return true;
  }
  // Suppress cross-origin "Script error." from Monaco editor web workers
  if (message === 'Script error.' && !source) {
    return true;
  }
};

// 6. Bubble-phase error listener — runs AFTER capture handlers but still
//    before webpack-dev-server's overlay reads the event in some setups.
//    Belt-and-braces for the cross-origin Monaco worker case.
window.addEventListener("error", (e) => {
  if (e.message === 'Script error.' && !e.filename) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
}, false);

// 7. Hide dev-server / CRA runtime-error overlay when it surfaces nothing more
//    than the cross-origin "Script error." (always harmless — comes from
//    Monaco's web workers when their cross-origin scripts dispose). Genuine
//    runtime errors keep the overlay visible because their text doesn't match
//    the narrow regex below.
//
//    Match strategy: scan ANY iframe (CRA / webpack-dev-server / react-error-overlay
//    use different ids depending on version) and any element with role=dialog
//    that contains only the harmless pattern. Two things must both hold:
//      (a) Text contains "Script error." or "ERROR Script error."
//      (b) Text does NOT contain a real source line like "src/" or ".js:"
//          followed by anything other than the bundle.js handleError stack.
if (typeof document !== 'undefined') {
  const HARMLESS_PATTERNS = [
    /^\s*ERROR\s+Script error\./i,
    /Script error\.\s+at handleError\b/,
  ];
  const REAL_ERROR_HINTS = [
    /\bat\s+\w+\s+\(http:\/\/[^)]*\/static\/js\/[^)]*\.chunk\.js/, // user code in chunks
    /\bsrc\/[A-Za-z0-9_\-./]+\.(js|jsx|ts|tsx)/, // source-mapped origin
    /\bTypeError\b|\bReferenceError\b|\bSyntaxError\b|\bRangeError\b/,
  ];

  const isHarmlessText = (raw) => {
    const txt = (raw || '').trim();
    if (!txt) return false;
    if (!HARMLESS_PATTERNS.some(re => re.test(txt))) return false;
    // Don't hide overlays that also surface real errors below the harmless one.
    if (REAL_ERROR_HINTS.some(re => re.test(txt))) return false;
    return true;
  };

  const peekText = (el) => {
    try {
      // Read direct textContent first.
      let txt = el.textContent || '';
      // Try shadow DOM (webpack-dev-server uses one).
      if (el.shadowRoot) txt += '\n' + (el.shadowRoot.textContent || '');
      // Try iframe contentDocument (same-origin only — CRA's overlay is
      // same-origin so we can read it).
      try {
        const doc = el.contentDocument;
        if (doc) txt += '\n' + (doc.body?.textContent || '');
      } catch { /* cross-origin — ignore */ }
      return txt;
    } catch {
      return '';
    }
  };

  const isHarmlessNode = (node) => {
    if (!node || node.nodeType !== 1) return false;
    const tag = node.tagName;
    // Any iframe is suspect (CRA, webpack-dev-server, react-error-overlay).
    if (tag === 'IFRAME') return isHarmlessText(peekText(node));
    // Some setups use a custom element for the overlay.
    if (tag && tag.includes('-')) return isHarmlessText(peekText(node));
    return false;
  };

  const sweep = (root) => {
    try {
      const all = root.querySelectorAll?.('iframe, [role="dialog"]');
      if (!all) return;
      all.forEach((el) => {
        if (isHarmlessNode(el)) el.remove();
      });
    } catch { /* ignore */ }
  };

  const observer = new MutationObserver((mutations) => {
    let needsSweep = false;
    for (const m of mutations) {
      for (const n of m.addedNodes) {
        if (isHarmlessNode(n)) {
          n.remove();
        } else if (n && n.nodeType === 1) {
          needsSweep = true;
        }
      }
      // Re-check overlays whose text mutated (CRA renders content async).
      if (m.type === 'characterData' || m.target?.tagName === 'IFRAME') {
        needsSweep = true;
      }
    }
    if (needsSweep) sweep(document);
  });

  const startObserving = () => {
    if (!document.body) {
      window.requestAnimationFrame(startObserving);
      return;
    }
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
    sweep(document);
    // Periodic sweep as a final safety net — runs once a second, only ever
    // touches harmless overlays. Cheap.
    setInterval(() => sweep(document), 1000);
  };
  startObserving();
}
